import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  Dispatch,
  SetStateAction,
} from 'react';
import { useApolloClient } from '@apollo/client';

import { useAppState } from 'src/components/context';
import {
  cartAnalyticsItems,
  cartGoodsValue,
  pushEcommerce,
} from 'src/utils/analytics';
import { parsePrice } from 'src/utils/price';
import { CART, UPDATE_ITEM_QUANTITIES } from 'src/utils/cart/operations';
import {
  applyQuantityChange,
  hasChanges,
  mergePendingChange,
  PendingChanges,
  toQuantityInputs,
} from 'src/utils/cart/pendingChanges';

const PENDING_STORAGE_KEY = 'cartPendingChanges';

interface CartContextProps {
  showCart: boolean;
  setShowCart: Dispatch<SetStateAction<boolean>>;
  closeCart: () => void;
  /**
   * Applies a quantity change to the basket on screen straight away and remembers it
   * for the next flush. Fires no request. `0` removes the line.
   */
  queueQuantityChange: (key: string, quantity: number) => void;
  /**
   * Sends everything queued as one mutation and adopts the server's cart. Resolves
   * `true` when the server is in step with what the user sees — including when there
   * was nothing to send, in which case no request is made at all. Resolves `false`
   * after a failure, having already reconciled local state from the server.
   */
  flushCart: () => Promise<boolean>;
}

const CartContext = createContext<CartContextProps | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

const readStoredPending = (): PendingChanges => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = window.localStorage.getItem(PENDING_STORAGE_KEY);

    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error parsing pending cart changes:', error);

    return {};
  }
};

/**
 * Owns the basket's pending edits as well as its visibility.
 *
 * It lives here rather than in Cart.tsx because this provider is mounted once at the
 * root (gatsby-browser.js) while Cart is rendered by Layout and remounts on every route
 * change — pending edits held in the drawer would be lost exactly when the user
 * navigates to checkout. Sitting at the root also puts `flushCart` within reach of the
 * add-to-cart call sites, which must not mutate a server cart that is behind this one.
 */
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [showCart, setShowCart] = useState<boolean>(false);
  const { cart, setCart } = useAppState();
  const client = useApolloClient();

  /**
   * A ref, not state: a burst of clicks on "−" has to accumulate synchronously, and a
   * flush triggered in the same tick must see every intent recorded so far.
   */
  const pendingRef = useRef<PendingChanges>(readStoredPending());
  /**
   * The quantity the server held when a line was first touched, so a change that walks
   * back to it can be dropped instead of flushed. Committed state on purpose: a burst
   * of clicks in one tick must all measure against the same starting point.
   */
  const baselineRef = useRef<Record<string, number>>({});
  const cartRef = useRef(cart);
  /** Serialises flushes so two of them can never race the same session. */
  const queueRef = useRef<Promise<boolean>>(Promise.resolve(true));

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  /**
   * view_cart, reported when the drawer opens.
   *
   * It lives in the provider rather than in Cart.tsx because Cart is rendered by Layout
   * and remounts on every route change, while this provider is mounted once at the root
   * — an effect over there would report a basket view for each navigation made with the
   * drawer already open. Call sites flip the flag with a toggle rather than an opener,
   * so what is watched is the transition, not a function.
   */
  const wasCartOpen = useRef(false);

  useEffect(() => {
    if (showCart && !wasCartOpen.current) {
      pushEcommerce('view_cart', {
        currency: 'EUR',
        value: cartGoodsValue(cartRef.current),
        items: cartAnalyticsItems(cartRef.current),
      });
    }

    wasCartOpen.current = showCart;
  }, [showCart]);

  const writePending = useCallback((pending: PendingChanges) => {
    pendingRef.current = pending;

    if (!hasChanges(pending)) {
      baselineRef.current = {};
    }

    if (typeof window === 'undefined') return;

    try {
      if (hasChanges(pending)) {
        window.localStorage.setItem(
          PENDING_STORAGE_KEY,
          JSON.stringify(pending)
        );
      } else {
        window.localStorage.removeItem(PENDING_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving pending cart changes:', error);
    }
  }, []);

  const queueQuantityChange = useCallback(
    (key: string, quantity: number) => {
      const next = Math.max(0, quantity);
      const line = cartRef.current?.contents?.nodes?.find(
        node => node.key === key
      );

      if (!(key in baselineRef.current)) {
        baselineRef.current[key] = line?.quantity ?? 0;
      }

      /**
       * Reported here, at the press, rather than when the change reaches WooCommerce:
       * the flush happens on drawer close and carries a whole burst of edits at once,
       * which is neither when nor how the visitor removed anything.
       *
       * Measured against what is on screen, not against baselineRef — that holds the
       * quantity the server last confirmed, so a line walked 3 → 2 → 1 would report
       * the second press as two units gone rather than one.
       */
      if (line && next < (line.quantity ?? 0)) {
        const price = parsePrice(line.product?.node?.price);
        const removed = (line.quantity ?? 0) - next;

        pushEcommerce('remove_from_cart', {
          currency: 'EUR',
          value: price * removed,
          items: [
            {
              item_id: `${line.product?.node?.databaseId}`,
              item_name: line.product?.node?.name,
              ...(price > 0 ? { price } : {}),
              quantity: removed,
            },
          ],
        });
      }

      writePending(
        mergePendingChange(
          pendingRef.current,
          key,
          next,
          baselineRef.current[key]
        )
      );
      // Functional form: several changes in one tick must each build on the last.
      setCart(previous => applyQuantityChange(previous, key, next));
    },
    [setCart, writePending]
  );

  const runFlush = useCallback(async (): Promise<boolean> => {
    // Loops so anything queued while a request was in flight goes out with it.
    while (hasChanges(pendingRef.current)) {
      const pending = pendingRef.current;

      writePending({});

      try {
        const { data } = await client.mutate({
          mutation: UPDATE_ITEM_QUANTITIES,
          variables: { input: { items: toQuantityInputs(pending) } },
        });

        const cart = data?.updateItemQuantities?.cart;

        if (!cart) {
          throw new Error('updateItemQuantities returned no cart');
        }

        setCart(cart);
        client.writeQuery({ query: CART, data: { cart } });
      } catch (error) {
        console.error('Could not update cart:', error);

        /**
         * Never leave the optimistic guess standing after a failure — checkout is
         * driven by this cart, so it has to match what the backend actually holds.
         */
        try {
          const { data } = await client.query({
            query: CART,
            fetchPolicy: 'network-only',
          });

          if (data?.cart) {
            setCart(data.cart);
          }
        } catch (refetchError) {
          console.error('Could not reload cart:', refetchError);
        }

        return false;
      }
    }

    return true;
  }, [client, setCart, writePending]);

  const flushCart = useCallback((): Promise<boolean> => {
    const next = queueRef.current.then(runFlush, runFlush);

    queueRef.current = next;

    return next;
  }, [runFlush]);

  const closeCart = useCallback(() => {
    setShowCart(false);
    void flushCart();
  }, [flushCart]);

  // Changes left over from a previous visit (tab closed mid-edit) go out on load.
  useEffect(() => {
    if (hasChanges(pendingRef.current)) {
      void flushCart();
    }
    // Once, on mount: this is a replay, not a reaction to anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best effort before the tab goes away, so the replay above stays the rare path.
  useEffect(() => {
    const flushBeforeLeaving = () => {
      if (hasChanges(pendingRef.current)) {
        void flushCart();
      }
    };

    const flushWhenHidden = () => {
      if (document.visibilityState === 'hidden') {
        flushBeforeLeaving();
      }
    };

    window.addEventListener('pagehide', flushBeforeLeaving);
    document.addEventListener('visibilitychange', flushWhenHidden);

    return () => {
      window.removeEventListener('pagehide', flushBeforeLeaving);
      document.removeEventListener('visibilitychange', flushWhenHidden);
    };
  }, [flushCart]);

  // Reset showCart to false on component unmount
  useEffect(() => {
    return () => {
      setShowCart(false);
    };
  }, []);

  const value = useMemo(
    () => ({
      showCart,
      setShowCart,
      closeCart,
      queueQuantityChange,
      flushCart,
    }),
    [showCart, closeCart, queueQuantityChange, flushCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextProps => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
